import { useCallback, useEffect, useState } from 'react';
import { ClbCheckbox } from '@celebration/react/ClbCheckbox/ClbCheckbox';
import { useTranslation } from 'react-i18next';
import ICarriersResponse from '../../../../../carriers/interfaces/ICarriersResponse';
import IUserEditFormInfosProps from '../interfaces/IUserEditFormInfosProps';
import { useToast } from '../../../../../../app/contexts/toast/toast.context';
import Input from '../../../../../../common/components/input';
import SelectAutocomplete from '../../../../../../common/components/select-autocomplete';
import UserFormValidationService from '../../../user-form/services/user-form.validation.service';
import Shape from '../../../../../../common/components/shape';
import { ReactComponent as SpanIcon } from '../../../../../../common/assets/svg/spam.svg';
import { ICheckboxProps } from '../../../../../branches/components/branch-form/components/branch-form-regions/interfaces/ICheckboxProps';
import UserAccountType from '../../enums/user-account-type.enum';
import InternalUserTypes from '../../enums/internal-user-types.enum';
import ThirdUserTypes from '../../enums/third-user-types.enum';
import UserEditFormService from '../../services/user-edit-form.service';
import CarriersService from '../../../../../carriers/services/carriers.service';
import Auth from '../../../../../../app/contexts/auth';

function UserEditFormInfos({
    handleUserFormChange,
    userFormData,
    inputErrors,
    userAccountType,
}: Readonly<IUserEditFormInfosProps>) {
    const { t } = useTranslation();

    const userDataSubtitle = t('DadosUsuarios');
    const systemsSubtitle = t('SistemasPermitidos');

    const { isThirdUser } = Auth.useAuth();

    const { setToast } = useToast();

    const [carriersList, setCarriersList] = useState([] as ICarriersResponse[]);

    const selectedCheckBox = useCallback(
        (props: ICheckboxProps) => {
            const systemsNotUpdated = [...userFormData.systems];

            systemsNotUpdated.forEach(system => {
                if (system.id === props.id) {
                    system.isChecked = props.checked;
                }
            });

            handleUserFormChange(userFormData);
        },
        [handleUserFormChange, userFormData],
    );

    const handleCarrierSearch = async (value: string) => {
        const response = await CarriersService.getCarriersList(1, 20, {
            carrierName: value,
        }).catch(err => {
            setToast('negative', 'Erro', err.message);
        });

        return response?.data ?? [];
    };
    const carriersResult = (data: ICarriersResponse[]) =>
        setCarriersList([...data]);

    useEffect(() => {
        CarriersService.getCarriersList(1, 20, { carrierName: '' })
            .then(response => {
                const { data } = response;
                setCarriersList([...data]);
            })
            .catch(err => {
                setToast('negative', 'Erro', err.message);
            });
    }, [setToast, t]);

    const renderSelectCarrier = () => {
        return (
            <SelectAutocomplete
                label={t('Transportadora')}
                placeholder={t('Transportadora')}
                options={carriersList.map(carrier => ({
                    label: carrier.tradeName,
                    value: carrier.id,
                }))}
                value={userFormData.userCarrier?.name}
                dataTestId="user-edit-carrier"
                className="user-edit-carrier"
                onChange={(e: any) => {
                    const carrier = carriersList.find(
                        carrier => carrier.id === e,
                    );
                    handleUserFormChange({
                        property: 'userCarrier',
                        newValue: {
                            carrierId: e,
                            name: carrier?.tradeName,
                            sulaHubId: carrier?.sulaHubId,
                        },
                    });
                }}
                onSearch={handleCarrierSearch}
                onFetchDataResults={carriersResult}
                error={!!inputErrors.userCarrier?.carrierId}
                helperText={UserFormValidationService.getError(
                    inputErrors.userCarrier?.carrierId,
                    t,
                )}
            />
        );
    };

    const renderUserData = () => {
        let accountType;
        if (userAccountType === UserAccountType.internal) {
            accountType = InternalUserTypes;
        } else {
            accountType = ThirdUserTypes;
        }
        return (
            <>
                <h1>{userDataSubtitle}</h1>

                <div className="user-data-div">
                    <Input
                        id="user-edit-name"
                        label={t('Nome')}
                        value={userFormData?.name}
                        className="user-name"
                        placeholder={t('InsiraNomeUsuario')}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'name',
                                newValue: e.target.value,
                            })
                        }
                        disabled={userAccountType === UserAccountType.internal}
                        error={!!inputErrors.name}
                        helperText={UserFormValidationService.getError(
                            inputErrors.name,
                            t,
                        )}
                        dataTestId="user-edit-name"
                    />

                    <Input
                        id="user-edit-email"
                        label={t('Email')}
                        value={userFormData?.email}
                        className="user-email"
                        placeholder={t('InsiraEmailUsuario')}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'email',
                                newValue: e.target.value,
                            })
                        }
                        disabled
                        error={!!inputErrors.email}
                        helperText={UserFormValidationService.getError(
                            inputErrors.email,
                            t,
                        )}
                        dataTestId="user-edit-email"
                    />

                    <SelectAutocomplete
                        label={t('TipoUsuario')}
                        placeholder={t('TipoUsuario')}
                        defaultValue={Number(userFormData?.type)}
                        value={Number(userFormData?.type)}
                        options={Object.keys(accountType)
                            .filter(x => !Number.isNaN(Number(x)))
                            .map(x => ({
                                label: UserEditFormService.TranslateUserTypesLabel(
                                    Number(x),
                                    userAccountType,
                                ),
                                value: Number(x),
                            }))}
                        onChange={(e: any) =>
                            handleUserFormChange({
                                property: 'type',
                                newValue: e,
                            })
                        }
                        dataTestId="user-edit-type"
                    />

                    {userAccountType === UserAccountType.third &&
                        !isThirdUser() &&
                        renderSelectCarrier()}
                </div>
            </>
        );
    };

    const renderUserAllowedSystems = () => {
        return (
            <>
                <h1>{systemsSubtitle}</h1>

                <div className="user-allowed-systems">
                    {userFormData.systems?.map(item => (
                        <ClbCheckbox
                            id={item.id}
                            key={item.id}
                            name="Checkbox"
                            checked={item.isChecked}
                            data-testid={`checkbox-${item.id}`}
                            onChange={(e: any) => {
                                selectedCheckBox({
                                    id: e.target.id,
                                    checked: e.target.checked,
                                });
                            }}
                        >
                            {item.label}
                        </ClbCheckbox>
                    ))}
                </div>
                {inputErrors.systems && (
                    <span className="systems-checked-error">
                        <SpanIcon />
                        {UserFormValidationService.getError(
                            inputErrors.systems,
                            t,
                        )}
                    </span>
                )}
            </>
        );
    };

    return (
        <Shape>
            {renderUserData()}
            {renderUserAllowedSystems()}
        </Shape>
    );
}

export default UserEditFormInfos;
